import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from '@/components/ui/sonner';
import { ErrorBoundary } from '@/components/shared/ErrorBoundary';
import { useNexusStore } from '@/stores/nexusStore';
import AppShell from '@/components/layout/AppShell';
import LoginPage from '@/pages/LoginPage';
import CommandCenter from '@/pages/CommandCenter';
import IncidentsPage from '@/pages/IncidentsPage';
import IncidentDetailPage from '@/pages/IncidentDetailPage';
import ChangesPage from '@/pages/ChangesPage';
import RequestsPage from '@/pages/RequestsPage';
import ProblemsPage from '@/pages/ProblemsPage';
import AssetsPage from '@/pages/AssetsPage';
import AssetDetailPage from '@/pages/AssetDetailPage';
import AgentsPage from '@/pages/AgentsPage';
import KnowledgePage from '@/pages/KnowledgePage';
import AnalyticsPage from '@/pages/AnalyticsPage';
import SettingsPage from '@/pages/SettingsPage';
import TopologyPage from '@/pages/TopologyPage';
import NlpQueryPage from '@/pages/NlpQueryPage';
import AgentConfigPage from '@/pages/AgentConfigPage';
import DocsPage from '@/pages/DocsPage';
import FullDocsPage from '@/pages/FullDocsPage';
import CompliancePage from '@/pages/CompliancePage';
import AuditTrailPage from '@/pages/AuditTrailPage';
import MspDashboardPage from '@/pages/MspDashboardPage';
import RunbookMarketplacePage from '@/pages/RunbookMarketplacePage';
import ExecutiveRoiPage from '@/pages/ExecutiveRoiPage';
import SecurityPage from '@/pages/SecurityPage';
import StatusPage from '@/pages/StatusPage';
import OnboardingPage from '@/pages/OnboardingPage';
import IntegrationsPage from '@/pages/IntegrationsPage';
import TeamPage from '@/pages/TeamPage';
import SlaNotificationsPage from '@/pages/SlaNotificationsPage';
import AgentScalingPage from '@/pages/AgentScalingPage';
import PasswordResetPage from '@/pages/PasswordResetPage';

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 2, staleTime: 5000, refetchOnWindowFocus: false } },
});

const ProtectedRoute = ({ children }) => {
  const isAuthenticated = useNexusStore((s) => s.isAuthenticated);
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return children;
};

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/password-reset" element={<PasswordResetPage />} />
          <Route element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
            <Route index element={<CommandCenter />} />
            <Route path="/incidents" element={<IncidentsPage />} />
            <Route path="/incidents/:id" element={<IncidentDetailPage />} />
            <Route path="/changes" element={<ChangesPage />} />
            <Route path="/requests" element={<RequestsPage />} />
            <Route path="/problems" element={<ProblemsPage />} />
            <Route path="/security" element={<SecurityPage />} />
            <Route path="/assets" element={<AssetsPage />} />
            <Route path="/assets/:id" element={<AssetDetailPage />} />
            <Route path="/topology" element={<TopologyPage />} />
            <Route path="/agents" element={<AgentsPage />} />
            <Route path="/agents/scaling" element={<AgentScalingPage />} />
            <Route path="/knowledge" element={<KnowledgePage />} />
            <Route path="/analytics" element={<AnalyticsPage />} />
            <Route path="/analytics/query" element={<NlpQueryPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/settings/agents" element={<AgentConfigPage />} />
            <Route path="/settings/compliance" element={<CompliancePage />} />
            <Route path="/settings/audit" element={<AuditTrailPage />} />
            <Route path="/msp" element={<MspDashboardPage />} />
            <Route path="/runbooks" element={<RunbookMarketplacePage />} />
            <Route path="/executive" element={<ExecutiveRoiPage />} />
            <Route path="/settings/security" element={<SecurityPage />} />
            <Route path="/settings/status" element={<StatusPage />} />
            <Route path="/onboarding" element={<OnboardingPage />} />
            <Route path="/settings/integrations" element={<IntegrationsPage />} />
            <Route path="/settings/team" element={<TeamPage />} />
            <Route path="/settings/sla" element={<SlaNotificationsPage />} />
            <Route path="/docs" element={<DocsPage />} />
            <Route path="/docs/full" element={<FullDocsPage />} />
          </Route>
        </Routes>
      </BrowserRouter>
      <Toaster
        position="bottom-right"
        toastOptions={{
          style: {
            background: 'var(--nexus-bg-elevated)',
            border: '1px solid var(--nexus-border)',
            color: 'var(--nexus-text-primary)',
            fontFamily: 'var(--font-sans)',
            fontSize: '12px',
          },
        }}
      />
    </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
